const defaultClock = {
  now: Date.now,
  setTimeout: (callback, delay) => setTimeout(callback, delay),
  clearTimeout: (timer) => clearTimeout(timer),
};

function sourceTimestamp(data) {
  const timestamp = typeof data?.DateUpdated === 'string' ? Date.parse(data.DateUpdated) : NaN;
  return Number.isFinite(timestamp) ? timestamp : null;
}

export function normalizeContractStats(contractId, data, receivedAt) {
  const price = data?.LastTradePrice;
  if (typeof price !== 'number' || !Number.isFinite(price) || price < 0 || price > 1) return null;
  return {
    contractId,
    priceCents: Math.round(price * 100),
    receivedAt,
    updatedAt: sourceTimestamp(data),
    initial: false,
  };
}

export function createLivePriceFeed({
  contractIds,
  getDatabase,
  onTick,
  onUnavailable = () => {},
  onStatus = () => {},
  clock = defaultClock,
}) {
  const ids = [...new Set(contractIds)].filter((id) => Number.isSafeInteger(id) && id > 0);
  const subscriptions = [];
  const previous = new Map();
  const startedAt = clock.now();
  let stopped = false;
  let readinessTimer = null;

  function stop() {
    if (stopped) return;
    stopped = true;
    clock.clearTimeout(readinessTimer);
    readinessTimer = null;
    for (const { ref, callback } of subscriptions) {
      try {
        ref.off('value', callback);
      } catch {
        // A deleted Firebase app must not prevent cleanup of remaining refs.
      }
    }
    subscriptions.length = 0;
    previous.clear();
  }

  function fail(message) {
    if (stopped) return;
    stop();
    onStatus({ state: 'error', message });
  }

  function receive(contractId, data) {
    if (stopped) return;
    const prev = previous.get(contractId);
    const updatedAt = sourceTimestamp(data);
    if (updatedAt !== null && prev?.updatedAt != null && updatedAt < prev.updatedAt) return;
    const tick = normalizeContractStats(contractId, data, clock.now());
    previous.set(contractId, {
      updatedAt: updatedAt ?? prev?.updatedAt ?? null,
      priceCents: tick?.priceCents ?? null,
    });
    if (!tick) {
      if (!prev || prev.priceCents !== null) onUnavailable(contractId);
      return;
    }
    if (tick.priceCents === prev?.priceCents) return;
    tick.initial = prev?.priceCents == null;
    onTick(tick);
    if (!stopped) {
      onStatus({
        state: 'receiving',
        message: tick.initial ? 'Latest prices received' : 'Receiving live price updates',
      });
    }
  }

  function connect() {
    readinessTimer = null;
    if (stopped) return;
    let database;
    try {
      database = getDatabase();
    } catch {
      fail('Unable to connect to the price feed.');
      return;
    }
    if (!database) {
      if (clock.now() - startedAt >= 10000) {
        fail('Price feed is unavailable.');
      } else {
        readinessTimer = clock.setTimeout(connect, 100);
      }
      return;
    }
    onStatus({ state: 'waiting', message: 'Waiting for prices from the live feed' });
    try {
      for (const contractId of ids) {
        if (stopped) break;
        const ref = database.ref(`contractStats/${contractId}`);
        const callback = (snapshot) => {
          if (!stopped) receive(contractId, snapshot.val());
        };
        subscriptions.push({ ref, callback });
        ref.on('value', callback, () => fail('Access to the price feed failed.'));
      }
    } catch {
      fail('Unable to subscribe to the price feed.');
    }
  }

  if (ids.length) {
    onStatus({ state: 'connecting', message: 'Connecting to the live price feed' });
    connect();
  }
  return { stop };
}
